import { Box, Flex, Text, HStack } from "@chakra-ui/react";
import { useState } from "react";
import { AppSelect } from "../../components";
import { colors } from "../../theme";
import { useGetMallAnalytics } from "../../services/useQueries";
import { formatCount, formatOMR } from "../../utils/format";

// Periods the server knows, in Oman time.
const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "Last 7 days" },
  { value: "month", label: "Last 30 days" },
  { value: "year", label: "Last 12 months" },
  { value: "all", label: "All time" },
];
const COMPARED_WITH = {
  today: "the same time yesterday",
  week: "the 7 days before",
  month: "the 30 days before",
  year: "the 12 months before",
};

// Bar labels from the server's keys: "2026-09-30T14" (hour), "2026-09-30" (day), "2026-09" (month).
const shortLabel = (key, bucket) => {
  if (bucket === "hour") return `${key.slice(11, 13)}:00`;
  if (bucket === "day") {
    return new Date(`${key}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  }
  const month = new Date(`${key}-15T12:00:00Z`).toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  return `${month} '${key.slice(2, 4)}`;
};
const longLabel = (key, bucket) => {
  if (bucket === "hour") {
    const day = new Date(`${key.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
    return `${day}, ${key.slice(11, 13)}:00 to ${key.slice(11, 13)}:59`;
  }
  if (bucket === "day") {
    return new Date(`${key}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  }
  return new Date(`${key}-15T12:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
};

// Revenue per hour, day or month, drawn to scale (the tallest bar is the
// period's best, labelled above the chart). Hover a bar for its figures.
function RevenueBars({ series, bucket }) {
  const max = Math.max(0, ...series.map((b) => b.revenue));
  const labelEvery = bucket === "hour" ? 6 : series.length > 14 ? 5 : 1;
  return (
    <Box>
      <Text fontSize="xs" color={colors.textSecondary} mb={1} minH="16px">
        {max > 0 ? `Highest: ${formatOMR(max)}` : "No delivered orders in this period"}
      </Text>
      <Flex align="flex-end" gap="2px" h="140px" borderBottom="1px solid" borderColor={colors.disabled}>
        {series.map((b) => (
          <Flex
            key={b.key}
            flex="1"
            h="100%"
            align="flex-end"
            title={`${longLabel(b.key, bucket)}: ${formatOMR(b.revenue)} from ${b.delivered} delivered ${
              b.delivered === 1 ? "order" : "orders"
            } (${b.orders} placed)`}
          >
            <Box
              w="100%"
              h={max > 0 ? `${(b.revenue / max) * 100}%` : "0"}
              minH={b.revenue > 0 ? "2px" : "0"}
              bg={colors.primary}
              borderTopRadius="2px"
            />
          </Flex>
        ))}
      </Flex>
      <Flex gap="2px" mt={1}>
        {series.map((b, i) => (
          <Box key={b.key} flex="1" position="relative" h="16px">
            {i % labelEvery === 0 && (
              <Text position="absolute" left="0" fontSize="10px" color={colors.textSecondary} whiteSpace="nowrap">
                {shortLabel(b.key, bucket)}
              </Text>
            )}
          </Box>
        ))}
      </Flex>
    </Box>
  );
}

function StatTile({ label, value, note, bg }) {
  return (
    <Box bg={bg} borderRadius="md" px={4} py={3} flex="1" minW="0">
      <Text fontSize="xl" fontWeight="700" color={colors.text} mb={1} truncate>
        {value}
      </Text>
      <Text fontSize="xs" color={colors.textSecondary}>
        {label}
      </Text>
      {note && (
        <Text fontSize="xs" color={colors.textSecondary}>
          {note}
        </Text>
      )}
    </Box>
  );
}

/**
 * One mall's figures for a period: revenue from delivered orders (to the baisa),
 * orders placed, shops, a comparison with the period before when there is one,
 * and revenue per hour, day or month.
 */
export default function MallRevenueCard({ mall }) {
  const [period, setPeriod] = useState("week");
  const { data: response, isLoading } = useGetMallAnalytics(mall?.id, period);
  const data = response?.data || response || {};
  const stats = data.statistics || {};
  const revenueTrend = data.trends?.revenue || {};
  const change = revenueTrend.change;
  const comparedWith = COMPARED_WITH[period];

  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={5} borderWidth="1px" borderColor="gray.100">
      <Flex justify="space-between" align="center" mb={4} gap={3}>
        <Text fontWeight="700" fontSize="md" color={colors.text} truncate>
          {mall?.name || "Mall"}
        </Text>
        <Box w="160px" flexShrink={0}>
          <AppSelect value={period} onChange={(value) => setPeriod(value || "week")} options={PERIOD_OPTIONS} />
        </Box>
      </Flex>

      <HStack gap={3} mb={4} align="stretch">
        <StatTile label="Revenue, delivered orders" value={isLoading ? "—" : formatOMR(stats.totalRevenue)} bg="#EFF6FF" />
        <StatTile
          label="Orders placed"
          value={isLoading ? "—" : formatCount(stats.totalOrders)}
          note={isLoading ? null : `${formatCount(stats.deliveredOrders)} delivered · ${formatCount(stats.cancelledOrders)} cancelled`}
          bg="#FEF3C7"
        />
        <StatTile label="Shops" value={isLoading ? "—" : formatCount(stats.totalRestaurants)} bg="#FFF7ED" />
      </HStack>

      <Text fontSize="sm" color={colors.textSecondary} mb={3} minH="20px">
        {!isLoading && comparedWith && change !== null && change !== undefined
          ? change === 0
            ? `Revenue is the same as ${comparedWith}.`
            : `Revenue is ${change > 0 ? "up" : "down"} ${Math.abs(change)}% on ${comparedWith}.`
          : ""}
      </Text>

      {isLoading || !Array.isArray(data.series) ? (
        <Box h="176px" />
      ) : (
        <RevenueBars series={data.series} bucket={data.period?.bucket || "day"} />
      )}
    </Box>
  );
}
